"use client";

import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";

const ACCENT = "#f97316";

const money = (n) =>
  (Number(n) || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const hoursFmt = (n) => (Number(n) || 0).toFixed(2);
const cls = (...c) => c.filter(Boolean).join(" ");

const TYPE_META = {
  hourly: { label: "Hourly", tint: "#3b82f6" },
  pieceworker: { label: "Piecework", tint: ACCENT },
};

const STATUS_META = {
  processed: { label: "Locked", tint: "#34d399" },
  exported: { label: "Exported", tint: "#60a5fa" },
  pending: { label: "Draft", tint: "#facc15" },
};

const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const downloadBase64File = (base64, filename) => {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], { type: XLSX_MIME });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Default selection: entries on a union work order, for weeks that start in
// the chosen month. Weeks that only overlap the month (their Monday is in the
// previous month) and non-union entries stay unticked until picked.
const isRemitted = (e) => e.remittance?.status === "remitted";
const isDefaultSelected = (e) =>
  e.inMonth && e.union !== "non-union" && !isRemitted(e);

const selectCls =
  "p-2 rounded-lg bg-[#161a20] border border-[#2a2f37] text-gray-200 focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-xs outline-none";

const Tag = ({ tint, children }) => (
  <span
    className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border"
    style={{
      color: tint,
      borderColor: `${tint}40`,
      backgroundColor: `${tint}14`,
    }}
  >
    {children}
  </span>
);

const Stat = ({ label, value, sub }) => (
  <div className="rounded-lg px-3 py-2 bg-[#12151b] border border-[#22262e]">
    <p className="text-[10px] uppercase tracking-wider text-gray-500">
      {label}
    </p>
    <p className="text-sm font-semibold text-gray-100 mt-0.5">{value}</p>
    {sub && <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

/**
 * Union Dues export picker.
 *
 * Month → pick (filter + tick) the payroll entries to remit → builds the two
 * DALI Excel workbooks (Hourly/ICI + Piecework) on the server and downloads
 * them.
 *
 * Props
 *  open          boolean
 *  month         "YYYY-MM"  work month being remitted
 *  initialUnion  union title to pre-filter on ("all" = none)
 *  apiBase       e.g. ".../api"
 *  getHeaders    () => ({ Authorization })
 *  onClose       () => void
 *  onDone        (type, title, message) => void   — shown after a successful export
 */
export default function UnionDuesExportModal({
  open,
  month,
  initialUnion = "all",
  apiBase,
  getHeaders,
  onClose,
  onDone,
}) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [employerNumberSet, setEmployerNumberSet] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [filters, setFilters] = useState({
    search: "",
    payType: "all",
    union: "all",
    week: "all",
    status: "all",
    remit: "all",
    inMonth: "all",
  });
  const [remitMonth, setRemitMonth] = useState(format(new Date(), "yyyy-MM"));
  const [exporting, setExporting] = useState(false);
  const [markRemitted, setMarkRemitted] = useState(false);
  const [reference, setReference] = useState("");

  // yearly template management
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);

  const monthLabel = useMemo(
    () => (month ? format(parseISO(`${month}-01`), "MMMM yyyy") : ""),
    [month],
  );

  // ── load candidate entries whenever the modal opens ──────────────────────
  useEffect(() => {
    if (!open || !month) return;
    let cancelled = false;

    const load = async () => {
      const startUnion =
        initialUnion && !["all", "All Unions"].includes(initialUnion)
          ? initialUnion
          : "all";

      setLoading(true);
      setError("");
      setEntries([]);
      setSelected(new Set());
      setRemitMonth(format(new Date(), "yyyy-MM"));
      setMarkRemitted(false);
      setReference("");
      setFilters({
        search: "",
        payType: "all",
        union: startUnion,
        week: "all",
        status: "all",
        remit: "all",
        inMonth: "all",
      });

      try {
        const res = await axios.get(
          `${apiBase}/payroll/union-dues/export/entries`,
          { headers: getHeaders(), params: { month } },
        );
        if (cancelled) return;
        const list = res.data.entries || [];
        setEntries(list);
        setEmployerNumberSet(res.data.employerNumberSet !== false);
        setSelected(
          new Set(
            list
              .filter(
                (e) =>
                  isDefaultSelected(e) &&
                  (startUnion === "all" || e.union === startUnion),
              )
              .map((e) => e._id),
          ),
        );
      } catch (err) {
        if (!cancelled)
          setError(err.response?.data?.message || err.message || "Load failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, month]);

  // ── templates: one pair per union; the toggle picks hourly vs piecework ───
  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${apiBase}/payroll/union-dues/templates`, {
        headers: getHeaders(),
      });
      setTemplates(res.data.unions || []);
    } catch {
      /* non-critical */
    }
  };

  useEffect(() => {
    if (open) {
      setShowTemplates(false);
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── filter options ───────────────────────────────────────────────────────
  const unionOptions = useMemo(
    () => Array.from(new Set(entries.map((e) => e.union))).sort(),
    [entries],
  );

  const weekOptions = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      if (!map.has(e.weekStart))
        map.set(
          e.weekStart,
          `${format(parseISO(e.weekStart), "MMM d")} – ${format(parseISO(e.weekEnd), "MMM d")}`,
        );
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [entries]);

  const visible = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return entries
      .filter(
        (e) =>
          (!q ||
            e.worker.toLowerCase().includes(q) ||
            String(e.employeeNumber).toLowerCase().includes(q)) &&
          (filters.payType === "all" || e.payType === filters.payType) &&
          (filters.union === "all" || e.union === filters.union) &&
          (filters.week === "all" || e.weekStart === filters.week) &&
          (filters.status === "all" || e.status === filters.status) &&
          (filters.inMonth === "all" ||
            (filters.inMonth === "in" && e.inMonth) ||
            (filters.inMonth === "out" && !e.inMonth)) &&
          (filters.remit === "all" ||
            (filters.remit === "open" && !isRemitted(e)) ||
            (filters.remit === "remitted" && isRemitted(e))),
      )
      .sort(
        (a, b) =>
          a.worker.localeCompare(b.worker, undefined, {
            sensitivity: "base",
          }) ||
          (a.workDate || a.weekStart).localeCompare(
            b.workDate || b.weekStart,
          ) ||
          a.payType.localeCompare(b.payType),
      );
  }, [entries, filters]);

  const filtersActive =
    filters.search ||
    filters.payType !== "all" ||
    filters.union !== "all" ||
    filters.week !== "all" ||
    filters.status !== "all" ||
    filters.remit !== "all" ||
    filters.inMonth !== "all";

  // ── selection ────────────────────────────────────────────────────────────
  // entries already marked remitted can't be picked again
  const pickable = visible.filter((e) => !isRemitted(e));
  const visibleSelectedCount = pickable.filter((e) =>
    selected.has(e._id),
  ).length;
  const allVisibleSelected =
    pickable.length > 0 && visibleSelectedCount === pickable.length;

  const toggleOne = (id) =>
    setSelected((prev) => {
      const ent = entries.find((x) => x._id === id);
      if (ent && isRemitted(ent)) return prev;
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) pickable.forEach((e) => next.delete(e._id));
      else pickable.forEach((e) => next.add(e._id));
      return next;
    });

  const resetSelection = () =>
    setSelected(new Set(entries.filter(isDefaultSelected).map((e) => e._id)));

  // ── summary of what the files will contain (mirrors the server rules) ────
  const summary = useMemo(() => {
    const byUser = new Map();
    let lines = 0;
    let gross = 0;
    let hours = 0;
    entries.forEach((e) => {
      if (!selected.has(e._id)) return;
      lines += 1;
      gross += e.amount;
      if (e.payType === "hourly") hours += e.hours;
      if (!byUser.has(e.userId))
        byUser.set(e.userId, {
          name: e.worker,
          hourly: false,
          piece: false,
          dues: 0,
        });
      const u = byUser.get(e.userId);
      if (e.payType === "hourly") u.hourly = true;
      else u.piece = true;
      u.dues = Math.max(u.dues, e.duesAmount || 0);
    });

    let hourlyWorkers = 0;
    let pieceWorkers = 0;
    let hourlyDues = 0;
    let pieceDues = 0;
    const noDues = [];
    byUser.forEach((u) => {
      if (u.hourly) {
        hourlyWorkers += 1;
        hourlyDues += u.dues;
      }
      // dues are only ever charged once: hourly wins over piecework
      if (u.piece) {
        pieceWorkers += 1;
        if (!u.hourly) pieceDues += u.dues;
      }
      if (u.dues <= 0) noDues.push(u.name);
    });

    return {
      lines,
      workers: byUser.size,
      gross,
      hours,
      hourlyWorkers,
      pieceWorkers,
      hourlyDues,
      pieceDues,
      noDues,
    };
  }, [entries, selected]);

  // ── export ───────────────────────────────────────────────────────────────
  // Record entries as remitted without generating files (e.g. already sent)
  const handleRecordOnly = async () => {
    if (selected.size === 0 || exporting) return;
    setExporting(true);
    setError("");
    try {
      const res = await axios.post(
        `${apiBase}/payroll/union-dues/remittances/manual`,
        { month, remitMonth, entryIds: Array.from(selected), reference },
        { headers: getHeaders() },
      );
      onClose();
      if (onDone)
        onDone(
          "success",
          "Marked as Remitted",
          `${res.data.recorded} entr${res.data.recorded === 1 ? "y" : "ies"} recorded as remitted for ${monthLabel}.`,
        );
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Save failed");
    } finally {
      setExporting(false);
    }
  };

  const handleExport = async () => {
    if (selected.size === 0 || exporting) return;
    setExporting(true);
    setError("");
    try {
      const res = await axios.post(
        `${apiBase}/payroll/union-dues/export`,
        {
          month,
          remitMonth,
          entryIds: Array.from(selected),
          markRemitted,
          reference,
        },
        { headers: getHeaders() },
      );
      const files = res.data.files || [];
      const warnings = res.data.warnings || [];

      for (let i = 0; i < files.length; i++) {
        downloadBase64File(files[i].base64, files[i].filename);
        // small gap so browsers don't swallow the second download
        if (i < files.length - 1) await new Promise((r) => setTimeout(r, 400));
      }

      onClose();
      if (onDone) {
        const names = files
          .map(
            (f) =>
              `${f.filename} (${f.rows} worker${f.rows === 1 ? "" : "s"}, ${money(f.dues)} dues)`,
          )
          .join(" · ");
        onDone(
          "success",
          markRemitted ? "Exported & Marked Remitted" : "Union Dues Exported",
          `${names}${warnings.length ? ` — Note: ${warnings.join(" ")}` : ""}`,
        );
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] px-4">
      <div
        className="rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col"
        style={{ backgroundColor: "#161a20", border: "1px solid #2a2f37" }}
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-3 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-white">
              Union Dues Export — {monthLabel}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Tick the payroll entries to remit. Each worker appears once per
              file; dues are charged once — on the hourly sheet if they have
              hourly entries, otherwise on piecework.
            </p>
          </div>
          <button
            onClick={() => setShowTemplates((v) => !v)}
            className={cls(
              "ml-auto text-xs px-3 py-1.5 rounded-lg border transition-colors whitespace-nowrap",
              showTemplates
                ? "border-orange-500 text-orange-300 bg-orange-500/10"
                : "border-[#2a2f37] text-gray-300 hover:text-orange-300 hover:border-orange-500/50",
            )}
          >
            Templates
          </button>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300 p-1 rounded-md hover:bg-[#1c2027] transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Banners */}
        {!employerNumberSet && !loading && (
          <div className="mx-6 mb-3 px-3 py-2 rounded-lg text-xs text-amber-300 bg-amber-400/10 border border-amber-400/20">
            No employer number is set in Company Profile, so the template's
            existing number will be used. Add one under Company Profile → Edit
            Profile.
          </div>
        )}
        {error && (
          <div className="mx-6 mb-3 px-3 py-2 rounded-lg text-xs text-red-300 bg-red-500/10 border border-red-500/20">
            {error}
          </div>
        )}

        {/* Templates status — read only; configured per union in Company Profile */}
        {showTemplates && (
          <div className="mx-6 mb-3 rounded-lg bg-[#12151b] border border-[#22262e] p-3">
            <p className="text-[11px] text-gray-500 mb-2">
              Remittance templates are set up per union in{" "}
              <span className="text-gray-300">Company Profile → Unions</span>.
            </p>
            {templates.length === 0 && (
              <p className="text-xs text-gray-500">
                No unions are set up in the Company Profile yet.
              </p>
            )}
            {templates.map((u) => (
              <div
                key={u.union}
                className="flex flex-wrap items-center gap-x-4 gap-y-1 py-1.5 border-t border-[#1c2027] first:border-t-0"
              >
                <span className="text-sm font-medium text-gray-100 min-w-[120px]">
                  {u.union}
                </span>
                {u.templates.map((t) => (
                  <span
                    key={t.kind}
                    className={cls(
                      "text-xs",
                      t.source === "missing"
                        ? "text-amber-400"
                        : "text-gray-400",
                    )}
                  >
                    {t.label}:{" "}
                    {t.source === "uploaded"
                      ? t.filename
                      : t.source === "builtin"
                        ? "Built-in DALI"
                        : "missing — remitting blocked"}
                  </span>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="px-6 pb-3 flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={filters.search}
            onChange={(e) =>
              setFilters((f) => ({ ...f, search: e.target.value }))
            }
            placeholder="Search worker or employee #…"
            className={cls(selectCls, "w-56")}
          />
          <select
            value={filters.payType}
            onChange={(e) =>
              setFilters((f) => ({ ...f, payType: e.target.value }))
            }
            className={selectCls}
          >
            <option value="all">All pay types</option>
            <option value="hourly">Hourly</option>
            <option value="pieceworker">Piecework</option>
          </select>
          <select
            value={filters.union}
            onChange={(e) =>
              setFilters((f) => ({ ...f, union: e.target.value }))
            }
            className={selectCls}
          >
            <option value="all">All unions</option>
            {unionOptions.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
          <select
            value={filters.week}
            onChange={(e) =>
              setFilters((f) => ({ ...f, week: e.target.value }))
            }
            className={selectCls}
          >
            <option value="all">All weeks</option>
            {weekOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(e) =>
              setFilters((f) => ({ ...f, status: e.target.value }))
            }
            className={selectCls}
          >
            <option value="all">Draft + locked</option>
            <option value="processed">Locked only</option>
            <option value="pending">Draft only</option>
          </select>
          <select
            value={filters.inMonth}
            onChange={(e) =>
              setFilters((f) => ({ ...f, inMonth: e.target.value }))
            }
            className={selectCls}
          >
            <option value="all">Any work date</option>
            <option value="in">Worked in {monthLabel}</option>
            <option value="out">Worked outside month</option>
          </select>
          <select
            value={filters.remit}
            onChange={(e) =>
              setFilters((f) => ({ ...f, remit: e.target.value }))
            }
            className={selectCls}
          >
            <option value="all">Remitted + open</option>
            <option value="open">Not yet remitted</option>
            <option value="remitted">Already remitted</option>
          </select>
          {filtersActive && (
            <button
              onClick={() =>
                setFilters({
                  search: "",
                  payType: "all",
                  union: "all",
                  week: "all",
                  status: "all",
                  remit: "all",
                  inMonth: "all",
                })
              }
              className="text-xs text-orange-400 hover:text-orange-300 px-1"
            >
              Clear filters
            </button>
          )}
          <div className="ml-auto flex items-center gap-3 text-xs">
            <button
              onClick={resetSelection}
              className="text-gray-400 hover:text-orange-300 transition-colors"
              title="Re-select union entries for weeks starting in this month"
            >
              Reset to union entries
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="text-gray-400 hover:text-orange-300 transition-colors"
            >
              Clear all
            </button>
          </div>
        </div>

        {/* Table */}
        <div
          className="mx-6 rounded-xl overflow-hidden flex-1 min-h-[180px] flex flex-col"
          style={{ border: "1px solid #22262e" }}
        >
          <div className="overflow-y-auto flex-1">
            <table className="min-w-full text-sm">
              <thead
                className="sticky top-0 z-10"
                style={{ backgroundColor: "#161a20" }}
              >
                <tr className="text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-2 w-10 text-left">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      ref={(el) => {
                        if (el)
                          el.indeterminate =
                            visibleSelectedCount > 0 && !allVisibleSelected;
                      }}
                      onChange={toggleAllVisible}
                      disabled={visible.length === 0}
                      title="Select / deselect everything shown"
                      className="h-4 w-4 accent-orange-500 rounded bg-[#161a20] border-gray-600"
                    />
                  </th>
                  <th className="px-3 py-2 text-left">Worker</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Work date</th>
                  <th className="px-3 py-2 text-left">Pay week</th>
                  <th className="px-3 py-2 text-left">Union</th>
                  <th className="px-3 py-2 text-center">Lvl</th>
                  <th className="px-3 py-2 text-right">Hours</th>
                  <th className="px-3 py-2 text-right">Gross</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-10 text-center text-xs text-gray-500"
                    >
                      Loading payroll entries…
                    </td>
                  </tr>
                )}
                {!loading && visible.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-10 text-center text-xs text-gray-500"
                    >
                      {entries.length === 0
                        ? "No hourly or piecework payroll entries found for this month."
                        : "No entries match the current filters."}
                    </td>
                  </tr>
                )}
                {visible.map((e) => {
                  const isSel = selected.has(e._id);
                  const type = TYPE_META[e.payType] || {
                    label: e.payType,
                    tint: "#9ca3af",
                  };
                  const status = STATUS_META[e.status] || STATUS_META.pending;
                  const done = isRemitted(e);
                  return (
                    <tr
                      key={e._id}
                      onClick={() => toggleOne(e._id)}
                      className={cls(
                        "cursor-pointer transition-colors duration-100 border-t border-[#1c2027]",
                        done
                          ? "opacity-50 cursor-not-allowed"
                          : isSel
                            ? "bg-orange-500/[0.07]"
                            : "hover:bg-[#171b21]",
                      )}
                    >
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={isSel}
                          disabled={done}
                          onClick={(ev) => ev.stopPropagation()}
                          onChange={() => toggleOne(e._id)}
                          className="h-4 w-4 accent-orange-500 rounded bg-[#161a20] border-gray-600"
                        />
                      </td>
                      <td className="px-3 py-2 text-gray-200 whitespace-nowrap">
                        {e.worker}
                        <span className="ml-2 text-[11px] text-gray-600 font-mono">
                          #{e.employeeNumber}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <Tag tint={type.tint}>{type.label}</Tag>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                        {e.workDate ? (
                          <span className="text-gray-200">
                            {format(parseISO(e.workDate), "EEE MMM d")}
                          </span>
                        ) : (
                          <span
                            className="text-gray-500"
                            title="No linked timesheet/log — judged by pay week"
                          >
                            n/a
                          </span>
                        )}
                        {!e.inMonth && (
                          <span
                            className="ml-2 text-[10px] text-amber-400"
                            title={
                              e.workDate
                                ? "Work was done outside this month"
                                : "Pay week starts outside this month"
                            }
                          >
                            outside month
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500 font-mono text-xs whitespace-nowrap">
                        {format(parseISO(e.weekStart), "MMM d")} –{" "}
                        {format(parseISO(e.weekEnd), "MMM d")}
                      </td>
                      <td
                        className={cls(
                          "px-3 py-2 text-xs whitespace-nowrap",
                          e.union === "non-union"
                            ? "text-gray-600"
                            : "text-gray-300",
                        )}
                      >
                        {e.union}
                      </td>
                      <td className="px-3 py-2 text-center text-gray-400 text-xs">
                        {e.level}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-300 font-mono text-xs">
                        {e.payType === "hourly" ? hoursFmt(e.hours) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-100 font-mono text-xs">
                        {money(e.amount)}
                      </td>
                      <td className="px-3 py-2">
                        {done ? (
                          <Tag tint="#34d399">
                            Remitted {e.remittance.workMonth}
                          </Tag>
                        ) : e.remittance ? (
                          <Tag tint="#60a5fa">Prepared</Tag>
                        ) : (
                          <Tag tint={status.tint}>{status.label}</Tag>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <p className="px-6 pt-2 text-[11px] text-gray-600">
          Showing {visible.length} of {entries.length} entries · Gross is the
          amount before tax · Entries are matched on the day the work was done
          (timesheet / piecework log date), so a pay week that spills into
          another month is split correctly. Entries tagged{" "}
          <span className="text-amber-400">outside month</span> weren't worked
          in {monthLabel} and aren't pre-selected.
        </p>

        {/* Summary */}
        <div className="px-6 pt-3 grid grid-cols-2 md:grid-cols-5 gap-2">
          <Stat
            label="Selected"
            value={`${summary.lines} entr${summary.lines === 1 ? "y" : "ies"}`}
            sub={`${summary.workers} worker${summary.workers === 1 ? "" : "s"}`}
          />
          <Stat
            label="Hourly (ICI) sheet"
            value={`${summary.hourlyWorkers} worker${summary.hourlyWorkers === 1 ? "" : "s"}`}
            sub={`${hoursFmt(summary.hours)} hrs · ${money(summary.hourlyDues)} dues`}
          />
          <Stat
            label="Piecework sheet"
            value={`${summary.pieceWorkers} worker${summary.pieceWorkers === 1 ? "" : "s"}`}
            sub={`${money(summary.pieceDues)} dues`}
          />
          <Stat label="Gross (pre-tax)" value={money(summary.gross)} />
          <Stat
            label="Total dues"
            value={money(summary.hourlyDues + summary.pieceDues)}
            sub={
              summary.noDues.length > 0
                ? `${summary.noDues.length} worker${summary.noDues.length === 1 ? "" : "s"} with no dues set`
                : undefined
            }
          />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <label className="block text-[11px] font-medium text-gray-400 mb-1">
              Remitting month
            </label>
            <input
              type="month"
              value={remitMonth}
              onChange={(e) => setRemitMonth(e.target.value)}
              className={selectCls}
            />
          </div>
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="block text-[11px] font-medium text-gray-400">
              Reference (cheque / confirmation #)
            </label>
            <input
              type="text"
              value={reference}
              maxLength={100}
              onChange={(e) => setReference(e.target.value)}
              className={selectCls}
              placeholder="optional"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={markRemitted}
              onChange={(e) => setMarkRemitted(e.target.checked)}
              className="h-4 w-4 accent-orange-500 rounded"
            />
            Mark these entries as remitted
          </label>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-300 bg-[#1c2027] rounded-lg hover:bg-[#22262e] transition-colors duration-150"
            >
              Cancel
            </button>
            <button
              onClick={handleRecordOnly}
              disabled={selected.size === 0 || exporting || loading}
              title="Mark as remitted without creating the Excel files"
              className="px-4 py-2 text-sm font-medium text-gray-200 bg-[#1c2027] rounded-lg hover:bg-[#22262e] transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Record only
            </button>
            <button
              onClick={handleExport}
              disabled={selected.size === 0 || exporting || loading}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:brightness-110 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: ACCENT }}
            >
              {exporting
                ? "Building…"
                : `Export ${summary.lines} entr${summary.lines === 1 ? "y" : "ies"} (Excel)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
